import { useCallback, useEffect, useRef, useState } from 'react'
import { SELECTION_HOLD_MS } from '../motion'
import { useVibeAgentStore } from '../store'
import type { GateAnswer, TranscriptEntry } from '../wire'
import { resolveGateCountdown, useGateCountdown } from './use-gate-countdown'
import { useVibeAgentApi } from './use-vibe-agent-api'

// Digit shortcuts must never fire while the user is typing (composer is a Quill
// contenteditable) or interacting with an overlay.
const DIGIT_GUARD_SELECTOR =
  'input, textarea, [contenteditable="true"], [role="dialog"], [role="menu"]'

export const useQuestionNavigation = (entry: TranscriptEntry) => {
  const gate = useVibeAgentStore((state) => state.gate)
  const { answerQuestions } = useVibeAgentApi()

  const questionData = entry.question_data
  const questions = questionData?.questions || []
  const interactionId = questionData?.interaction_id ?? ''

  const [activeTab, setActiveTab] = useState(0)
  const [selections, setSelections] = useState<Record<number, string[]>>({})
  const [customInputs, setCustomInputs] = useState<Record<number, string>>({})
  const [focusedOption, setFocusedOption] = useState(-1)

  // The backend can auto-answer this gate (background runs) — that arrives as a
  // store update marking question_data answered, so the answered view derives from
  // the entry for both live auto_answered and rehydration.
  const isAnswered = questionData?.is_answered ?? false
  const isActiveGate = !!interactionId && gate?.interactionId === interactionId

  // Countdown window comes from the active gate (null = parked, no timer).
  const countdownState = resolveGateCountdown(isActiveGate ? gate?.autoAnswerSeconds : null)
  const countdown = useGateCountdown(countdownState.armed && !isAnswered, countdownState.seconds)

  const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const clearPendingAdvance = () => {
    if (advanceTimer.current) {
      clearTimeout(advanceTimer.current)
      advanceTimer.current = null
    }
  }
  useEffect(() => clearPendingAdvance, [])

  // Single navigation path: manual paging cancels any pending auto-advance so a
  // stale timer can't yank the view after the user has moved on.
  const goToQuestion = useCallback((index: number) => {
    clearPendingAdvance()
    setActiveTab(index)
    setFocusedOption(-1)
  }, [])

  const handleOptionSelect = useCallback(
    (qIndex: number, label: string) => {
      const question = questions[qIndex]
      if (!question) return

      setCustomInputs((prev) => ({ ...prev, [qIndex]: '' }))

      if (question.multi_select) {
        setSelections((prev) => {
          const current = prev[qIndex] || []
          const updated = current.includes(label)
            ? current.filter((l) => l !== label)
            : [...current, label]
          return { ...prev, [qIndex]: updated }
        })
      } else {
        setSelections((prev) => ({ ...prev, [qIndex]: [label] }))
        clearPendingAdvance()
        if (questions.length > 1 && qIndex < questions.length - 1) {
          // Hold the selection on screen for a beat before advancing so the
          // pick visibly registers (Codex-style selection hold).
          advanceTimer.current = setTimeout(() => {
            advanceTimer.current = null
            setActiveTab(qIndex + 1)
            setFocusedOption(-1)
          }, SELECTION_HOLD_MS)
        }
      }
    },
    [questions],
  )

  const selectOptionByIndex = useCallback(
    (optionIndex: number) => {
      const option = questions[activeTab]?.options[optionIndex]
      if (!option) return
      setFocusedOption(optionIndex)
      handleOptionSelect(activeTab, option.label)
    },
    [questions, activeTab, handleOptionSelect],
  )

  // Digit shortcuts 1-9 select options globally while this gate is open, so the
  // card works without being focused — guarded away from editables and overlays.
  useEffect(() => {
    if (!isActiveGate || isAnswered) return

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return
      if (e.key < '1' || e.key > '9') return
      const target = e.target as HTMLElement | null
      if (target?.closest(DIGIT_GUARD_SELECTOR)) return
      const optionIndex = Number(e.key) - 1
      if (optionIndex >= (questions[activeTab]?.options.length ?? 0)) return
      e.preventDefault()
      selectOptionByIndex(optionIndex)
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isActiveGate, isAnswered, questions, activeTab, selectOptionByIndex])

  const handleCustomInputChange = (qIndex: number, value: string) => {
    setCustomInputs((prev) => ({ ...prev, [qIndex]: value }))
    if (value.trim()) {
      setSelections((prev) => ({ ...prev, [qIndex]: [] }))
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (isAnswered) return
    const tagName = (e.target as HTMLElement)?.tagName
    if (tagName === 'INPUT' || tagName === 'TEXTAREA') return

    const currentOptions = questions[activeTab]?.options || []

    switch (e.key) {
      case 'ArrowLeft':
        if (questions.length > 1 && activeTab > 0) {
          goToQuestion(activeTab - 1)
        }
        break
      case 'ArrowRight':
        if (questions.length > 1 && activeTab < questions.length - 1) {
          goToQuestion(activeTab + 1)
        }
        break
      case 'ArrowUp':
        e.preventDefault()
        setFocusedOption((prev) => (prev <= 0 ? currentOptions.length - 1 : prev - 1))
        break
      case 'ArrowDown':
        e.preventDefault()
        setFocusedOption((prev) => (prev >= currentOptions.length - 1 ? 0 : prev + 1))
        break
      case 'Enter':
        if (focusedOption >= 0 && focusedOption < currentOptions.length) {
          handleOptionSelect(activeTab, currentOptions[focusedOption].label)
        }
        break
    }
  }

  const canProceed = (qIndex: number) => {
    const selected = selections[qIndex] || []
    const custom = customInputs[qIndex]?.trim()
    return selected.length > 0 || !!custom
  }

  const allQuestionsAnswered = questions.every((_, idx) => canProceed(idx))

  const handleSubmit = () => {
    const answers: GateAnswer[] = questions.map((q, i) => {
      const selected = selections[i] || []
      const custom = customInputs[i]?.trim()
      let answer: string[] = ['Not answered']
      if (custom) answer = [custom]
      else if (selected.length > 0) answer = selected
      return { question: q.header, answer }
    })
    answerQuestions(interactionId, answers)
  }

  return {
    questionData,
    questions,
    isAnswered,
    activeTab,
    goToQuestion,
    focusedOption,
    setFocusedOption,
    selections,
    customInputs,
    countdown,
    countdownState,
    canProceed,
    allQuestionsAnswered,
    handleOptionSelect,
    handleCustomInputChange,
    handleKeyDown,
    handleSubmit,
  }
}
